import {
  PaymentCardCardPayinJsonldPaymentPaymentCardPayinRead,
  PaymentCardJsonldPaymentPaymentCardRead,
  PaymentPricingGridJsonldPaymentPricingGridRead,
} from '@/api/generated';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useModal } from 'context/Modal/Context';
import { MODAL_NAME } from 'context/Modal/MODAL_NAME';
import { useFormatPrice } from 'hooks/i18n';
import { usePaymentPricingGridApi } from 'hooks/queries/marketplace/offers';
import { useMyWallet } from 'hooks/queries/marketplace/wallet';
import usePostPayin from 'hooks/queries/usePostPayin';
import { VisualOptions } from 'types/forms';

const schema = (minimumAmount: number = 0) =>
  yup.object({
    planOptions: yup.string(),
    plan: yup
      .number()
      .required('plan-is-required')
      .transform((_value, originalValue) =>
        typeof originalValue === 'string' ? Number(originalValue.replace(/,/, '.')) : originalValue,
      )
      .min(minimumAmount),
    card: yup.object({
      externalId: yup.string(),
      number: yup.string(),
      type: yup.string(),
      expirationMonth: yup.string(),
      expirationYear: yup.string(),
      active: yup.boolean(),
    }),
  });

type FormValues = yup.InferType<ReturnType<typeof schema>>;

type UseWalletPlanOptionsArgs = {
  minimumAmount?: number;
  onPayinSuccess?: (payin: PaymentCardCardPayinJsonldPaymentPaymentCardPayinRead) => void;
};

const useWalletPlanOptions = ({ minimumAmount, onPayinSuccess }: UseWalletPlanOptionsArgs) => {
  const { query } = useRouter();
  const { replaceModal, closeModal } = useModal();

  const {
    queryResult: { data: wallet },
  } = useMyWallet();

  const { formatUserCurrency } = useFormatPrice();

  const FORMAT_GRID = (data: PaymentPricingGridJsonldPaymentPricingGridRead[]): VisualOptions =>
    data.reduce((acc: VisualOptions, cur) => {
      return {
        ...acc,
        [cur.amount || '']: {
          icon: 'fa-solid fa-coins',
          label: formatUserCurrency(cur.amount || 0),
          disabled: minimumAmount ? Number(cur.amount) < minimumAmount : false,
        },
      };
    }, {});

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(schema(minimumAmount)),
  });
  const { handleSubmit, setValue, watch, reset } = formMethods;

  const { data, isLoading } = usePaymentPricingGridApi();
  const planOptions = FORMAT_GRID(data?.['hydra:member'] || []);

  const handleSuccess = (payin: PaymentCardCardPayinJsonldPaymentPaymentCardPayinRead) => {
    reset();
    closeModal();
    onPayinSuccess?.(payin);
  };

  const postPayin = usePostPayin(handleSuccess);
  const openPaymentSummary = () => {
    const cardId = watch('card')?.externalId;
    const selectedPlan = Number(`${watch('plan')}`.replace(/,/, '.'));
    if (!selectedPlan || !cardId) {
      return;
    }
    replaceModal(MODAL_NAME.PAYMENT_SUMMARY, {
      // not Altered card but payment card
      card: watch('card'),
      amount: selectedPlan,
      postPayment: async () => {
        if (selectedPlan) {
          postPayin.mutateAsync({
            externalId: cardId,
            amount: selectedPlan,
            redirectURL: `${window.location.origin}${window.location.pathname}`,
          });
        }
      },
    });
  };

  const openPaymentListModal = () => {
    replaceModal(MODAL_NAME.PAYMENT_LIST, {
      hideAddNewCardIfMethodsAvailable: true,
      onChange: (card: PaymentCardJsonldPaymentPaymentCardRead) => {
        setValue('card', card);
        openPaymentSummary();
      },
    });
  };

  const submit = handleSubmit(() => {
    openPaymentListModal();
  });

  return {
    wallet,
    oldBalance: Array.isArray(query.oldBalance) ? query.oldBalance.join('') : query.oldBalance,
    planOptions,
    setValue,
    isLoading,
    formMethods,
    handleSubmit: submit,
  };
};

export default useWalletPlanOptions;
